import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { getHoldingsSummary, readSavedHoldingsSummary } from "@/lib/api";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, AreaChart, Area, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { TEST_IDS } from "@/lib/testIds";
import {
  Plus, Trash2, ArrowRight, GitCompareArrows, Sparkles, Download, Upload, Loader2, FileWarning, Star, Compass, Wallet, TrendingUp, Map,
} from "lucide-react";
import {
  buildCsv, downloadBlob, parseCsv,
  PORTFOLIO_CSV_HEADERS, portfolioToCsvRows, csvRowsToPortfolios,
} from "@/lib/csv";
import usePageSeo from "@/hooks/usePageSeo";
import TickerTape from "@/components/TickerTape";
import TotalHoldingsCard from "@/components/TotalHoldingsCard";

export default function Dashboard() {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  usePageSeo({
    title: "Dashboard — Dividend Portfolio Planner",
    description: "Your DPP home base — every saved portfolio, quick access to the builder, and your live Total Holdings card.",
    path: "/dashboard",
    noindex: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/portfolios");
      setPortfolios(res.data);
    } catch (e) {
      toast.error("Could not load portfolios");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this portfolio?")) return;
    try {
      await api.delete(`/portfolios/${id}`);
      toast.success("Portfolio deleted");
      setPortfolios((prev) => prev.filter((p) => p.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  // Export all portfolios (holdings flattened, one row per ticker) as RFC-4180 CSV.
  const exportCsv = () => {
    if (portfolios.length === 0) { toast.error("No portfolios to export"); return; }
    const rows = portfolios.flatMap(portfolioToCsvRows);
    downloadBlob(
      buildCsv(PORTFOLIO_CSV_HEADERS, rows),
      `dpp-portfolios-${new Date().toISOString().slice(0, 10)}.csv`,
    );
    toast.success(`Exported ${portfolios.length} portfolio${portfolios.length === 1 ? "" : "s"} (${rows.length} row${rows.length === 1 ? "" : "s"})`);
  };

  const exportOne = (p) => {
    const rows = portfolioToCsvRows(p);
    const safeName = (p.name || "portfolio").replace(/[^a-z0-9-_]+/gi, "_").toLowerCase();
    downloadBlob(
      buildCsv(PORTFOLIO_CSV_HEADERS, rows),
      `dpp-${safeName}-${new Date().toISOString().slice(0, 10)}.csv`,
    );
    toast.success(`Exported "${p.name}"`);
  };

  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState({ parsed: [], existingNames: new Set() });

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-selecting same file re-fires
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      const parsed = csvRowsToPortfolios(rows);
      if (parsed.length === 0) { toast.error("No portfolios found in CSV"); return; }
      const existingNames = new Set(portfolios.map((p) => p.name));
      setPreviewData({ parsed, existingNames });
      setPreviewOpen(true);
    } catch (err) {
      toast.error(`Import failed: ${err.message || err}`);
    }
  };

  const confirmImport = async () => {
    const parsed = previewData.parsed;
    if (parsed.length === 0) return;
    setImporting(true);
    setPreviewOpen(false);
    try {
      let ok = 0, failed = 0;
      for (const p of parsed) {
        try { await api.post("/portfolios", p); ok++; }
        catch (err) {
          failed++;
          const detail = err?.response?.data?.detail;
          if (err?.response?.status === 402) {
            toast.error(detail || "Free-tier limit reached", {
              action: { label: "Upgrade", onClick: () => nav("/pricing") },
              duration: 8000,
            });
            break;
          }
        }
      }
      if (ok) toast.success(`Imported ${ok} portfolio${ok === 1 ? "" : "s"}${failed ? ` (${failed} failed)` : ""}`);
      else if (failed && failed === parsed.length) toast.error("Import failed — check limits or CSV format");
      await load();
    } catch (err) {
      toast.error(`Import failed: ${err.message || err}`);
    } finally { setImporting(false); }
  };

  return (
    <>
      <TickerTape />
      <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">Your workbench</div>
          <h1 className="serif text-4xl sm:text-5xl mt-2">Saved scenarios</h1>
          <p className="text-muted-foreground mt-2 max-w-lg">
            Each card below is one hypothetical portfolio. Open it to edit, backtest, and get an AI explanation.
          </p>
          {/* Discreet re-summon chip for returning users. Dispatches the same
              `dp:open-tour` event fired by the landing hero + nav + chatbot,
              so the checklist reappears bottom-right regardless of prior
              dismissal state. Kept as a link-style pill (no filled bg) so
              it never competes with the top-right primary CTAs. */}
          <button
            type="button"
            onClick={() => {
              try { window.dispatchEvent(new CustomEvent("dp:open-tour")); } catch (_) {}
            }}
            data-testid="dashboard-restart-tour"
            className="mt-3 inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-secondary transition-colors"
            title="Reopen the 12-step Get Started checklist"
          >
            <Map className="w-3 h-3" /> Take Get Started again
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onImportFile}
            data-testid="dashboard-import-input"
          />
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="dashboard-import-csv"
          >
            {importing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> Import CSV</>
            )}
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={exportCsv}
            disabled={portfolios.length === 0}
            data-testid="dashboard-export-csv"
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button className="rounded-full" onClick={() => nav("/portfolio/new")}>
            <Plus className="w-4 h-4 mr-2" /> New portfolio
          </Button>
        </div>
      </div>

      <TotalHoldingsCard />
      <WatchlistPanel />
      <HoldingsPanel />

      {/* Compare Portfolios card — surfaces the saved-scenarios comparison
          tool (/compare). Only rendered when the user has 2+ saved
          portfolios since Compare needs at least two scenarios to be
          meaningful. */}
      {!loading && portfolios.length >= 2 && (
        <Card
          data-testid="dashboard-compare-card"
          className="card-hover border-secondary/30 bg-gradient-to-br from-secondary/[0.03] to-transparent overflow-hidden"
        >
          <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-12 h-12 rounded-lg bg-secondary/15 text-secondary grid place-items-center shrink-0">
              <GitCompareArrows className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="mono text-[10px] uppercase tracking-[0.25em] text-secondary">Side-by-side view</div>
              <h3 className="serif text-xl sm:text-2xl mt-1">Compare Portfolios</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                Stack any 2–4 of your <b>{portfolios.length} saved portfolios</b> next to each other — CAGR, yield-on-cost, projected monthly income, max drawdown, and Sharpe ratio all on one screen. Great for A/B testing DRIP vs cash, or high-yield vs growth-focused sleeves.
              </p>
            </div>
            <Button
              onClick={() => nav("/compare")}
              className="rounded-full shrink-0 w-full sm:w-auto"
              data-testid="dashboard-compare-cta"
            >
              Open Compare <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1,2,3].map((i) => (
            <div key={i} className="h-40 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : portfolios.length === 0 ? (
        <Card data-testid={TEST_IDS.dashboardEmpty} className="border-dashed">
          <CardContent className="p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-primary/10 text-primary grid place-items-center mx-auto">
              <Sparkles className="w-6 h-6" />
            </div>
            <h2 className="serif text-3xl mt-4">Nothing here yet.</h2>
            <p className="text-muted-foreground mt-2 max-w-md mx-auto">
              Try a starter portfolio like <span className="mono">SCHD 60% · JEPI 20% · VYM 20%</span> from 2015 to today.
            </p>
            <Button className="mt-6 rounded-full" onClick={() => nav("/portfolio/new")}>
              Build my first portfolio <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolios.map((p) => (
            <PortfolioCardWithChart
              key={p.id}
              p={p}
              onExport={exportOne}
              onDelete={remove}
            />
          ))}
        </div>
      )}

      <ImportPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        parsed={previewData.parsed}
        existingNames={previewData.existingNames}
        onConfirm={confirmImport}
      />
    </div>
    </>
  );
}

// ------------------------------------------------------------------
// PortfolioCardWithChart — one dashboard card per saved portfolio, now with
// a colorful interactive chart. Two charts stacked:
//   1. Allocation donut — one ring segment per ticker, color-cycled from a
//      6-color palette. Hover surfaces ticker · weight% · dollar allocation.
//   2. Projected-income bars — a stubby bar chart estimating the annualized
//      dividend a full portfolio-weight of each ticker throws off, using
//      a curated yield map (SCHD 3.5, JEPI 8.5, KO 3.5, etc.) so we don't
//      need to fire a network call per card at render time.
// ------------------------------------------------------------------

// 6-color palette that reads well against both light and dark mode. Order
// matters — first ticker of a portfolio wins the DPP amber signature so a
// user's biggest holding always feels branded.
const CHART_COLORS = ["#F59E0B", "#0F2A44", "#10B981", "#6366F1", "#06B6D4", "#F43F5E"];

// Yield estimates for the calculator's curated dividend universe. The card
// chart is illustrative — real yields come through /api/tickers/quote when
// the user opens a portfolio for backtesting. Missing tickers fall back to
// the S&P-avg 1.5% so unknown symbols still render a bar.
const YIELD_HINTS = {
  SCHD: 3.5, VYM: 2.7, VIG: 1.8, DGRO: 2.4, DVY: 3.6, HDV: 3.9, NOBL: 1.9,
  JEPI: 8.5, JEPQ: 10.2, QQQI: 12.5, DIVO: 4.6, SPYD: 4.5,
  O: 5.4, MAIN: 5.6, STAG: 4.2, ARR: 12.0,
  KO: 2.9, PEP: 3.7, JNJ: 3.3, PG: 2.5, MMM: 6.7, MO: 7.9, T: 5.9,
  ABBV: 3.4, LOW: 1.8, TGT: 3.9, XOM: 3.6, CVX: 4.6,
  MSFT: 0.7, AVGO: 1.3, TXN: 2.7, CSCO: 2.5, IBM: 3.0, AAPL: 0.4,
};

// Price-appreciation CAGR hints (10-year trailing avg, illustrative). Used
// to project the "price appreciation" area of the growth chart on each
// card. Backtests still run on real yfinance data when the user opens a
// portfolio — this map is only for the dashboard visual.
const CAGR_HINTS = {
  SCHD: 11.5, VYM: 8.2, VIG: 10.5, DGRO: 11.0, DVY: 7.5, HDV: 7.0, NOBL: 8.5,
  JEPI: 4.0, JEPQ: 6.0, QQQI: 5.0, DIVO: 8.5, SPYD: 7.0,
  O: 3.0, MAIN: 6.0, STAG: 7.5, ARR: -2.0,
  KO: 5.5, PEP: 6.0, JNJ: 4.5, PG: 7.5, MMM: -2.0, MO: 3.0, T: 0.5,
  ABBV: 12.0, LOW: 15.0, TGT: 8.0, XOM: 6.0, CVX: 6.5,
  MSFT: 26.0, AVGO: 35.0, TXN: 15.0, CSCO: 8.5, IBM: 5.0, AAPL: 24.0,
};

function PortfolioCardWithChart({ p, onExport, onDelete }) {
  // Composition data — filter to non-zero weights and sort desc so the
  // biggest slice is always drawn first (better donut readability).
  const composition = useMemo(() => {
    return (p.holdings || [])
      .filter((h) => Number(h.weight) > 0)
      .sort((a, b) => Number(b.weight) - Number(a.weight))
      .map((h, i) => ({
        ticker: (h.ticker || "").toUpperCase(),
        weight: Number(h.weight) || 0,
        allocation: (Number(h.weight) / 100) * Number(p.initial_investment || 0),
        yieldHint: YIELD_HINTS[(h.ticker || "").toUpperCase()] ?? 1.5,
        color: CHART_COLORS[i % CHART_COLORS.length],
      }));
  }, [p.holdings, p.initial_investment]);

  // Weighted-average yield across the portfolio — projected annual dividend
  // income is just weighted-yield × initial_investment.
  const wYield = composition.reduce((s, r) => s + (r.weight * r.yieldHint) / 100, 0);
  const projAnnualIncome = Math.round((wYield / 100) * Number(p.initial_investment || 0));

  // Weighted price-appreciation CAGR (illustrative — real numbers come from
  // backtest when user opens the portfolio). Falls back to 8% for unknown
  // tickers (long-run S&P 500 real return).
  const wCagr = composition.reduce((s, r) => {
    const cagr = CAGR_HINTS[r.ticker] ?? 8;
    return s + (r.weight * cagr) / 100;
  }, 0);

  // 10-year projection series — build one data point per year splitting
  // total value into three stacked buckets: principal (flat), price
  // appreciation (compounded at wCagr), and cumulative dividends
  // (compounded at wYield with DRIP reinvestment if the portfolio's drip
  // flag is on, else linear accumulation of cash payouts).
  const YEARS_PROJECTED = 10;
  const growthSeries = useMemo(() => {
    const principal = Number(p.initial_investment || 0);
    const priceR = wCagr / 100;
    const yieldR = wYield / 100;
    const rows = [];
    // t=0 baseline row so the areas anchor at $principal on the y-axis.
    rows.push({ year: 0, principal, appreciation: 0, dividends: 0 });
    for (let t = 1; t <= YEARS_PROJECTED; t++) {
      // Price appreciation = principal grown at wCagr − principal (delta only).
      const appreciation = principal * (Math.pow(1 + priceR, t) - 1);
      // Dividends: if DRIP, compound with price return; else linear.
      const dividends = p.drip
        ? principal * (Math.pow(1 + yieldR, t) - 1)
        : principal * yieldR * t;
      rows.push({
        year: t,
        principal,
        appreciation: Math.max(0, Math.round(appreciation)),
        dividends: Math.round(dividends),
      });
    }
    return rows;
  }, [p.initial_investment, p.drip, wCagr, wYield]);

  const endValue = growthSeries[growthSeries.length - 1];
  const totalAt10y = endValue.principal + endValue.appreciation + endValue.dividends;

  return (
    <Card data-testid={TEST_IDS.portfolioCard} className="card-hover overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <h3 className="serif text-2xl truncate" title={p.name}>{p.name}</h3>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
              {p.start_date} → {p.end_date}
            </div>
          </div>
          <div className="flex items-center shrink-0">
            <button
              data-testid="portfolio-export"
              onClick={() => onExport(p)}
              className="p-2 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
              aria-label={`Download ${p.name} CSV`}
              title="Download this portfolio"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              data-testid={TEST_IDS.portfolioDelete}
              onClick={() => onDelete(p.id)}
              className="p-2 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
              aria-label="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* --- Interactive donut + legend --- */}
        <div className="mt-5 grid grid-cols-[128px,1fr] gap-4 items-center">
          <div className="relative w-32 h-32" data-testid="portfolio-donut">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={composition}
                  dataKey="weight"
                  nameKey="ticker"
                  innerRadius={38}
                  outerRadius={60}
                  strokeWidth={2}
                  stroke="var(--card)"
                  isAnimationActive
                >
                  {composition.map((seg) => (
                    <Cell key={seg.ticker} fill={seg.color} />
                  ))}
                </Pie>
                <Tooltip
                  cursor={{ fill: "transparent" }}
                  contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, padding: "6px 10px" }}
                  formatter={(_, __, item) => {
                    const r = item?.payload;
                    if (!r) return null;
                    return [`${r.weight}% · $${Math.round(r.allocation).toLocaleString()}`, r.ticker];
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center label — total portfolio value */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Initial</div>
              <div className="serif text-sm tabular-nums">${(p.initial_investment / 1000).toFixed(1)}k</div>
            </div>
          </div>
          <div className="min-w-0 space-y-1.5">
            {composition.slice(0, 4).map((seg) => (
              <div key={seg.ticker} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: seg.color }} />
                <span className="mono text-[11px] font-medium truncate">{seg.ticker}</span>
                <span className="ml-auto tabular-nums text-muted-foreground">{seg.weight}%</span>
              </div>
            ))}
            {composition.length > 4 && (
              <div className="text-[10px] text-muted-foreground italic mono">
                +{composition.length - 4} more
              </div>
            )}
          </div>
        </div>

        {/* --- 10-year projected value area chart --- */}
        <div className="mt-5 pt-4 border-t border-border" data-testid="portfolio-growth-chart">
          <div className="flex items-center justify-between mb-1">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">10-year projection</div>
            <div className="serif text-lg tabular-nums text-foreground">
              ${(totalAt10y / 1000).toFixed(1)}k
              <span className="mono text-[10px] uppercase tracking-widest text-secondary ml-1">
                +{Math.round(((totalAt10y - endValue.principal) / endValue.principal) * 100)}%
              </span>
            </div>
          </div>
          <div className="h-24 -mx-1">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={growthSeries} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                <defs>
                  <linearGradient id={`g-principal-${p.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0F2A44" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0F2A44" stopOpacity={0.15} />
                  </linearGradient>
                  <linearGradient id={`g-appr-${p.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0.25} />
                  </linearGradient>
                  <linearGradient id={`g-div-${p.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.65} />
                    <stop offset="100%" stopColor="#F59E0B" stopOpacity={0.3} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="year" hide />
                <YAxis hide />
                <Tooltip
                  cursor={{ stroke: "var(--muted)" }}
                  contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 11, padding: "6px 10px" }}
                  formatter={(v, key) => [`$${Number(v).toLocaleString()}`, key === "principal" ? "Principal" : key === "appreciation" ? "Price appreciation" : "Dividend income"]}
                  labelFormatter={(l) => `Year ${l}`}
                />
                <Area type="monotone" stackId="1" dataKey="principal"    stroke="#0F2A44" strokeWidth={1.5} fill={`url(#g-principal-${p.id})`} />
                <Area type="monotone" stackId="1" dataKey="appreciation" stroke="#10B981" strokeWidth={1.5} fill={`url(#g-appr-${p.id})`} />
                <Area type="monotone" stackId="1" dataKey="dividends"    stroke="#F59E0B" strokeWidth={1.5} fill={`url(#g-div-${p.id})`} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          {/* Compact color-coded legend */}
          <div className="mt-1 flex items-center gap-3 mono text-[9px] uppercase tracking-widest text-muted-foreground">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#0F2A44]" />Principal</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#10B981]" />Price</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#F59E0B]" />Dividends</span>
            <span className="ml-auto">DRIP <span className={p.drip ? "text-primary" : "text-muted-foreground"}>{p.drip ? "ON" : "OFF"}</span></span>
          </div>
        </div>

        {/* --- Projected income mini-bar chart --- */}
        <div className="mt-4 pt-3 border-t border-border/50">
          <div className="flex items-center justify-between mb-1.5">
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Projected annual income
            </div>
            <div className="serif text-lg tabular-nums text-secondary" data-testid="portfolio-proj-income">
              ${projAnnualIncome.toLocaleString()}
            </div>
          </div>
          {/* Stacked horizontal bar — each ticker's contribution to income */}
          <div className="h-2 w-full rounded-full overflow-hidden bg-muted flex" title={`Weighted yield ${wYield.toFixed(2)}%`}>
            {composition.map((seg) => {
              const share = (seg.weight * seg.yieldHint) / 100;
              const pct = wYield > 0 ? (share / wYield) * 100 : 0;
              if (pct < 0.5) return null;
              return (
                <div
                  key={seg.ticker}
                  className="h-full transition-all duration-300 hover:opacity-80"
                  style={{ width: `${pct}%`, background: seg.color }}
                  title={`${seg.ticker} contributes $${Math.round((share / 100) * (p.initial_investment || 0)).toLocaleString()}/yr`}
                />
              );
            })}
          </div>
          <div className="mt-1 flex items-center justify-between mono text-[9px] uppercase tracking-widest text-muted-foreground">
            <span>Weighted yield <span className="text-foreground/80">{wYield.toFixed(2)}%</span></span>
            <span>DRIP <span className={p.drip ? "text-primary" : "text-muted-foreground"}>{p.drip ? "ON" : "OFF"}</span></span>
          </div>
        </div>

        <Link to={`/portfolio/${p.id}`} data-testid={TEST_IDS.portfolioOpen}>
          <Button variant="outline" className="w-full mt-5 rounded-full">Open <ArrowRight className="w-4 h-4 ml-2" /></Button>
        </Link>
      </CardContent>
    </Card>
  );
}



function ImportPreviewDialog({ open, onOpenChange, parsed, existingNames, onConfirm }) {
  const total = parsed.length;
  const conflicts = parsed.filter((p) => existingNames.has(p.name));
  const news = parsed.filter((p) => !existingNames.has(p.name));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="import-preview-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">Review CSV import</DialogTitle>
          <DialogDescription>
            {total} portfolio{total === 1 ? "" : "s"} parsed from your file. Review below before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4 flex-wrap text-sm">
          <div className="flex items-center gap-2">
            <Badge className="bg-primary text-primary-foreground mono text-[10px] uppercase">New</Badge>
            <span className="mono">{news.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="mono text-[10px] uppercase border-amber-500/40 text-amber-700 dark:text-amber-400">
              Same name
            </Badge>
            <span className="mono">{conflicts.length}</span>
          </div>
        </div>

        {conflicts.length > 0 && (
          <div className="flex gap-2 items-start rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs">
            <FileWarning className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
            <span className="text-foreground/80">
              {conflicts.length} portfolio name{conflicts.length === 1 ? "" : "s"} already exist in your workbench.
              Confirming will create <strong>duplicates</strong> — your existing portfolios are never overwritten.
            </span>
          </div>
        )}

        <div className="rounded-md border border-border overflow-hidden max-h-72 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60 sticky top-0">
              <tr className="text-left">
                <th className="px-3 py-1.5 mono text-[10px] uppercase tracking-widest text-muted-foreground">Portfolio</th>
                <th className="px-3 py-1.5 mono text-[10px] uppercase tracking-widest text-muted-foreground">Holdings</th>
                <th className="px-3 py-1.5 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Initial</th>
                <th className="px-3 py-1.5 mono text-[10px] uppercase tracking-widest text-muted-foreground">Range</th>
                <th className="px-3 py-1.5 mono text-[10px] uppercase tracking-widest text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody data-testid="import-preview-rows">
              {parsed.map((p, i) => {
                const conflict = existingNames.has(p.name);
                const totalW = p.holdings.reduce((s, h) => s + Number(h.weight || 0), 0);
                return (
                  <tr key={`${p.name}-${i}`} className="border-t border-border">
                    <td className="px-3 py-1.5">
                      <div className="serif text-sm">{p.name}</div>
                      <div className="mono text-[10px] text-muted-foreground">weights sum: {totalW.toFixed(2)}%</div>
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {p.holdings.slice(0, 5).map((h) => (
                          <span key={h.ticker} className="mono text-[10px] px-1.5 py-0.5 rounded bg-muted text-foreground/80 border border-border">
                            {h.ticker} {h.weight}%
                          </span>
                        ))}
                        {p.holdings.length > 5 && (
                          <span className="mono text-[10px] text-muted-foreground">+{p.holdings.length - 5}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 mono text-xs text-right">${Number(p.initial_investment).toLocaleString()}</td>
                    <td className="px-3 py-1.5 mono text-[10px] text-muted-foreground whitespace-nowrap">
                      {p.start_date} → {p.end_date}
                    </td>
                    <td className="px-3 py-1.5">
                      {conflict ? (
                        <Badge variant="outline" className="mono text-[10px] uppercase border-amber-500/40 text-amber-700 dark:text-amber-400">
                          Duplicate name
                        </Badge>
                      ) : (
                        <Badge className="bg-primary text-primary-foreground mono text-[10px] uppercase">New</Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="import-cancel">Cancel</Button>
          <Button
            onClick={onConfirm}
            className="rounded-full"
            data-testid="import-confirm"
          >
            <Upload className="w-4 h-4 mr-2" /> Import {total}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Watchlist panel ----------

const wlFmtPct = (v) => v == null ? "—" : `${Number(v).toFixed(2)}%`;
const wlFmt$ = (v) => v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function WatchlistPanel() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);

  const load = useCallback(async (withLive = false) => {
    setLoading(!withLive);
    setEnriching(withLive);
    try {
      const r = await api.get("/watchlist", { params: withLive ? { with_live: true } : {} });
      setItems(r.data.items || []);
    } catch { /* silent */ }
    finally { setLoading(false); setEnriching(false); }
  }, []);

  // First render: quick list. Then lazy-fetch live snapshots so page isn't blocked.
  useEffect(() => {
    load(false).then(() => load(true));
  }, [load]);

  const removeStar = async (ticker) => {
    try {
      await api.delete(`/watchlist/${ticker}`);
      setItems((prev) => prev.filter((i) => i.ticker !== ticker));
      toast.success(`Removed ${ticker}`);
    } catch { toast.error("Remove failed"); }
  };

  if (loading) return null;
  if (items.length === 0) {
    return (
      <Card className="mb-6 border-dashed" data-testid="watchlist-empty">
        <CardContent className="p-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-secondary/10 text-secondary grid place-items-center">
              <Star className="w-5 h-5" />
            </div>
            <div>
              <div className="serif text-lg">Your watchlist is empty</div>
              <div className="text-sm text-muted-foreground">
                Star tickers on the <Link to="/research" className="underline underline-offset-2 hover:text-primary">Research</Link> page to track them here with live yield & price.
              </div>
            </div>
          </div>
          <Button variant="outline" className="rounded-full" onClick={() => nav("/research")}>
            <Compass className="w-4 h-4 mr-2" /> Open Research
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6" data-testid="watchlist-panel">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 text-secondary fill-secondary" />
            <h2 className="serif text-2xl">Watchlist</h2>
            <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {items.length} ticker{items.length === 1 ? "" : "s"}
              {enriching && " · loading live data…"}
            </span>
          </div>
          <Button variant="ghost" size="sm" className="rounded-full" onClick={() => nav("/research")}>
            <Compass className="w-4 h-4 mr-1.5" /> Add more
          </Button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="watchlist-items">
          {items.map((it) => (
            <div
              key={it.ticker}
              className="rounded-md border border-border bg-card hover:border-primary/40 transition-colors px-4 py-3"
              data-testid={`watchlist-item-${it.ticker}`}
            >
              <div className="flex items-start justify-between gap-2">
                <button
                  onClick={() => nav(`/research?t=${it.ticker}`)}
                  className="text-left min-w-0 flex-1"
                >
                  <div className="mono text-lg font-semibold">{it.ticker}</div>
                  {it.name && <div className="text-xs text-muted-foreground truncate">{it.name}</div>}
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  {it.safety_grade && (
                    <span className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                      {it.safety_grade}
                    </span>
                  )}
                  <button
                    onClick={() => removeStar(it.ticker)}
                    className="text-muted-foreground hover:text-destructive transition-colors p-1"
                    aria-label={`Remove ${it.ticker}`}
                    data-testid={`watchlist-remove-${it.ticker}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <div className="mt-3 flex items-baseline justify-between">
                <div className="serif text-xl text-primary">{wlFmt$(it.price)}</div>
                <div className="mono text-xs text-secondary">{wlFmtPct(it.dividend_yield_pct)} <span className="text-muted-foreground">yield</span></div>
              </div>
              <div className="mt-2 flex gap-1.5">
                <button
                  onClick={() => nav(`/portfolio/new?seed=${it.ticker}`)}
                  className="mono text-[10px] uppercase tracking-widest text-secondary hover:text-primary transition-colors"
                  data-testid={`watchlist-add-portfolio-${it.ticker}`}
                >
                  + Portfolio
                </button>
                <span className="text-muted-foreground text-xs">·</span>
                <button
                  onClick={() => nav(`/research?t=${it.ticker}`)}
                  className="mono text-[10px] uppercase tracking-widest text-secondary hover:text-primary transition-colors"
                >
                  Research →
                </button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


// ---------- My Holdings panel ----------
const hpFmt$ = (v) => v == null ? "$0" : `$${Math.round(Number(v)).toLocaleString()}`;

function HoldingsPanel() {
  const nav = useNavigate();
  const [summary, setSummary] = useState(readSavedHoldingsSummary);
  const [loading, setLoading] = useState(() => !readSavedHoldingsSummary());

  useEffect(() => {
    (async () => {
      try {
        const r = await getHoldingsSummary();
        setSummary(r.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return null;
  if (!summary || summary.account_count === 0) {
    return (
      <Card className="mb-6 border-dashed" data-testid={TEST_IDS.dashboardHoldingsCard}>
        <CardContent className="p-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary grid place-items-center">
              <Wallet className="w-5 h-5" />
            </div>
            <div>
              <div className="serif text-lg">Track your real portfolio</div>
              <div className="text-sm text-muted-foreground">
                Paste positions from your broker to see live value, monthly income, and upcoming ex-dividend dates.
              </div>
            </div>
          </div>
          <Button className="rounded-full" onClick={() => nav("/holdings")} data-testid="dashboard-holdings-cta">
            <Wallet className="w-4 h-4 mr-2" /> Set up holdings
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6" data-testid={TEST_IDS.dashboardHoldingsCard}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-primary" />
            <h2 className="serif text-2xl">My holdings</h2>
            <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {summary.account_count} account{summary.account_count === 1 ? "" : "s"} · {summary.position_count} position{summary.position_count === 1 ? "" : "s"}
            </span>
          </div>
          <Button variant="ghost" size="sm" className="rounded-full" onClick={() => nav("/holdings")}>
            <TrendingUp className="w-4 h-4 mr-1.5" /> Open tracker
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <MetricPill label="Portfolio value" value={hpFmt$(summary.total_value)} tone="text-foreground" />
          <MetricPill label="Est. monthly income" value={hpFmt$(summary.forward_monthly_income)} tone="text-primary" />
          <MetricPill label="Annual income" value={hpFmt$(summary.forward_annual_income)} tone="text-secondary" />
          <MetricPill label="Yield on cost" value={summary.yield_on_cost_pct == null ? "—" : `${Number(summary.yield_on_cost_pct).toFixed(2)}%`} tone="text-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}

function MetricPill({ label, value, tone }) {
  return (
    <div className="rounded-md border border-border px-4 py-3">
      <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`serif text-2xl mt-1 ${tone}`}>{value}</div>
    </div>
  );
}
